import React, { useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import dayjs from 'dayjs';

import ApiWrapper from '../../hooks/useApi.hook';
import { useAppDispatch } from '../../hooks/useAppDispatcher.hook';
import { useModalManager } from '../../hooks/ModalManager.hook';

import { Employee } from '../../redux/Employee/Employee.type';
import { NotificationData } from '../../redux/Notification/NotificationType';
import { ALERT_ACTION_ENUM } from '../../types/action.types';

import { hexToRgba } from '../../services/hex.service';

import {
  getNotificationCounterThunk,
  getNotificationsListThunk,
  markAsReadNotificationThunk,
  removeNotificationThunk,
} from '../../redux/Notification/NotificationThunk';

import UserBadgeComponent from '../UserBadge.component';
import NotificationRemoveAlertComponent from '../Alert/Notification/NotificationRemoveAlert.component';
import IconButtonComponent from '../IconButton.component';
import { CANDIDATES_GROUPS } from '../../types/candidates.types';

import { StyledBaseIcon } from '../Icons/Base.icon';
import TrashIcon from '../Icons/Trash.icon';

import { RoutesConstant } from '../../constants/routes.constant';
import { DATE_TIME_FORMAT } from '../../constants/date.contant';
import {
  NOTIFICATION_ICON,
  NOTIFICATION_ICON_COLOR,
} from '../../constants/notification.constant';
import { checkUserRequiredRole } from '../../utils/getUserRolesByToken';
import { UserRole } from '../../types/userRole.types';
import useAuthHook from '../../hooks/useAuth.hook';

interface NotificationBlockProps {
  uuid: string;
  type: string;
  sendAt: number;
  readAt?: number;
  user?: Employee;
  notification?: NotificationData;
  isCommon?: boolean;
  isAccessRead?: boolean;
  isAccessDelete?: boolean;
}

const StyledNotificationAction = styled.div<{ isAdminRole: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;

  ${StyledBaseIcon} {
    color: ${({ theme }) => theme.colors.LABEL};
  }

  ${props => props.isAdminRole && 'pointer-events: auto'};
`;

const StyledNotificationDate = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
`;
const StyledNotificationUser = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
`;
const StyledNotificationType = styled.div`
  font-size: 14px;
  font-weight: 500;
`;
const StyledNotificationInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;
const StyledNotificationIcon = styled.div<{
  color?: string;
}>`
  display: flex;
  border-radius: 6px;
  width: 42px;
  height: 42px;
  align-items: center;
  justify-content: center;
  background: ${({ color, theme }) =>
    hexToRgba(theme.colors[color || 'LABEL'], 0.1)};
  color: ${({ color, theme }) => theme.colors[color || 'LABEL']};
`;
export const StyledNotificationBlock = styled(NavLink)`
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.BG};
  padding: 10px;
  gap: 10px;
  align-items: center;
  display: grid;
  grid-template-columns: 42px auto 42px;
  font-size: 12px;
  transition: all 0.3s;
`;

const StyledNotificationBlockWrapper = styled.div<{
  isRead: boolean;
  isHrRole: boolean;
  isAdminRole: boolean;
}>`
  &:hover {
    background: ${({ theme }) => theme.colors.CARD};
    box-shadow: ${props =>
      !props.isAdminRole && '0 3px 20px rgba(0, 0, 0, 0.15)'};
  }

  ${props => props.isRead && 'opacity: 0.5'};
  ${props => (props.isAdminRole || props.isHrRole) && 'pointer-events: none'};
`;

const NotificationBlockComponent = ({
  uuid,
  type,
  sendAt,
  readAt,
  notification,
  user,
  isCommon = false,
  isAccessRead = false,
  isAccessDelete: isAccess = false,
  ...restProps
}: NotificationBlockProps) => {
  const { user: currentUser } = useAuthHook();
  const dispatch = useAppDispatch();
  const { openModal, closeModal } = useModalManager();
  const { t } = useTranslation();
  const isHrRole = checkUserRequiredRole(currentUser.roles, [
    UserRole.HR,
    UserRole.DEV_HR,
  ]);
  const isAdminRole = checkUserRequiredRole(currentUser.roles, [
    UserRole.ADMIN,
    UserRole.SUPER_HR,
    UserRole.DEV_ADMIN,
    UserRole.DEV_SUPER_HR,
  ]);
  const isNotNotificationFromCurrentUser = user?.id !== currentUser.id;

  const handleConfirmDelete = (action?: ALERT_ACTION_ENUM) => {
    if (!action || !uuid) return;
    if (action && action !== ALERT_ACTION_ENUM.YES) return;
    ApiWrapper(removeNotificationThunk(uuid)).then((res: boolean) => {
      res && dispatch(getNotificationsListThunk({ ...restProps, page: 1 }));
    });
  };

  const handleNotificationClick = useCallback(
    (notification?: NotificationData) => {
      closeModal();

      if (!uuid || !notification || readAt) return;

      dispatch(markAsReadNotificationThunk(uuid)).then(() => {
        dispatch(getNotificationCounterThunk());
      });
    },
    [dispatch, uuid],
  );

  return (
    <StyledNotificationBlockWrapper
      isRead={!!readAt}
      isHrRole={isHrRole && isNotNotificationFromCurrentUser}
      isAdminRole={isAdminRole && isNotNotificationFromCurrentUser}
    >
      <StyledNotificationBlock
        to={
          notification && notification.path && isCommon
            ? `${RoutesConstant.CANDIDATES}/${CANDIDATES_GROUPS.ALL}${notification.path}`
            : '#'
        }
        onClick={() => handleNotificationClick(notification)}
      >
        <StyledNotificationIcon color={NOTIFICATION_ICON_COLOR[type]}>
          {NOTIFICATION_ICON[type]}
        </StyledNotificationIcon>
        <StyledNotificationInfo>
          <StyledNotificationType>
            {t(`notifications.types.${type}`)}
          </StyledNotificationType>
          <StyledNotificationUser>
            {isCommon ? (
              notification?.title && (
                <>
                  <div>{t('Candidate')}:</div>
                  <div>{notification.title}</div>
                </>
              )
            ) : (
              <>
                <div>{t('Author')}:</div>
                <div>
                  <UserBadgeComponent info={user} />
                </div>
              </>
            )}
          </StyledNotificationUser>
          <StyledNotificationDate>
            <div>{t('Date')}:</div>
            <div>{dayjs(sendAt).format(DATE_TIME_FORMAT)}</div>
          </StyledNotificationDate>
        </StyledNotificationInfo>
        {isAccess && (
          <StyledNotificationAction
            isAdminRole={
              !!readAt || (isAdminRole && isNotNotificationFromCurrentUser)
            }
          >
            <IconButtonComponent
              onClick={(ev: any) => {
                ev.preventDefault();
                ev.stopPropagation();

                openModal(NotificationRemoveAlertComponent, {
                  handleConfirm: handleConfirmDelete,
                });
              }}
            >
              <TrashIcon />
            </IconButtonComponent>
          </StyledNotificationAction>
        )}
      </StyledNotificationBlock>
    </StyledNotificationBlockWrapper>
  );
};

export default NotificationBlockComponent;
